import { HocuspocusProvider, WebSocketStatus } from '@hocuspocus/provider';
import type { PiccoloError, StatelessMessage, User, Y } from '@piccolohealth/pbs-common';
import React from 'react';
import { useAppContext } from '../../../hooks/useAppContext';
import { getRandomColor } from '../utils';

export type SaveStatus = 'saved' | 'unsaved' | 'saving';

export interface SaveInfo {
  saveStatus: SaveStatus;
  saveCount: number;
  lastSavedAt: number | null;
}

// Single ADT modelling the full connection + save lifecycle.
// Each variant carries only the data relevant to that phase.
//
// disconnected  — initial state, or grace period expired; shows offline UI
// connecting    — first connection in progress; waiting for sync
// connected     — websocket open and doc synced; editor is interactive
// reconnecting  — was connected, lost connection, grace period active;
//                 editor stays visible and editable while retrying
// error         — server sent a stateless error (e.g. document not found)
// outdated      — server replaced the document (e.g. a version restore); prompt a manual refresh
export type HocuspocusState =
  | { status: 'disconnected' }
  | { status: 'connecting' }
  | { status: 'connected'; save: SaveInfo }
  | { status: 'reconnecting'; disconnectedAt: number; save: SaveInfo }
  | { status: 'error'; error: PiccoloError }
  | { status: 'outdated' };

export interface UseHocuspocusProviderReturn {
  provider: HocuspocusProvider;
  state: HocuspocusState;
  yDoc: Y.Doc;
  user: User & {
    color: string;
  };
}

export const useHocuspocusProvider = (options: {
  documentId: string;
}): UseHocuspocusProviderReturn => {
  const { documentId } = options;
  const { config, organization, auth, user } = useAppContext();

  const [state, setState] = React.useState<HocuspocusState>({
    status: 'disconnected',
  });

  const provider = React.useMemo(() => {
    return new HocuspocusProvider({
      url: `${config.api.webUrl.replace('http', 'ws')}/hocuspocus/${organization.id}`,
      name: documentId,
      parameters: {
        organizationId: organization.id,
      },
      preserveConnection: false,
      connect: false,

      token: async () => {
        const { token } = await auth.getAccessToken();
        return token;
      },

      onStatus: ({ status: wsStatus }) => {
        setState((prev) => {
          switch (wsStatus) {
            case WebSocketStatus.Connected: {
              if (prev.status === 'reconnecting') {
                return { status: 'connected', save: prev.save };
              }

              if (prev.status === 'disconnected') {
                return { status: 'connecting' };
              }

              return prev;
            }
            case WebSocketStatus.Connecting: {
              if (prev.status === 'disconnected') {
                return { status: 'connecting' };
              }

              return prev;
            }
            case WebSocketStatus.Disconnected: {
              if (prev.status === 'connected') {
                return {
                  status: 'reconnecting',
                  disconnectedAt: Date.now(),
                  save: prev.save,
                };
              }

              if (prev.status === 'connecting') {
                return { status: 'disconnected' };
              }

              return prev;
            }
          }
        });
      },

      onStateless: ({ payload }) => {
        const message = JSON.parse(payload) as StatelessMessage;

        switch (message.type) {
          case 'reload': {
            setState({ status: 'outdated' });
            provider.disconnect();
            break;
          }
          case 'error': {
            console.error('Hocuspocus stateless error', message.error.type, message.error.message);
            setState({ status: 'error', error: message.error });
            provider.disconnect();
            break;
          }
          case 'saved':
            setState((prev) => {
              if (prev.status === 'connected' || prev.status === 'reconnecting') {
                return {
                  ...prev,
                  save: {
                    saveStatus: 'saving',
                    saveCount: prev.save.saveCount + 1,
                    lastSavedAt: message.timestamp,
                  },
                };
              }

              return prev;
            });

            setTimeout(() => {
              setState((prev) => {
                if (prev.status === 'connected' || prev.status === 'reconnecting') {
                  return { ...prev, save: { ...prev.save, saveStatus: 'saved' } };
                }

                return prev;
              });
            }, 1000);
            break;
        }
      },

      onAuthenticationFailed: async () => {
        await auth.getAccessToken(true);
        return provider.connect();
      },

      onSynced: ({ state: synced }) => {
        if (!synced) {
          return;
        }

        setState((prev) => {
          if (prev.status === 'connecting' || prev.status === 'disconnected') {
            return {
              status: 'connected',
              save: { saveStatus: 'saved', saveCount: 0, lastSavedAt: null },
            };
          }

          return prev;
        });
      },
    });
  }, [config.api.webUrl, organization.id, documentId, auth.getAccessToken]);

  // Connect the provider when the component is mounted.
  React.useEffect(() => {
    console.log(`Hocuspocus provider is being mounted for document ${documentId}. Connecting...`);
    provider.connect();

    // We are putting these props on the window object so we can access them in the e2e tests
    // and have a convenient way to refresh the auth token and reconnect the provider.
    window.__hocuspocusProvider = provider;
    window.__refreshAuthToken = () => auth.getAccessToken(true);

    return () => {
      console.log(
        `Hocuspocus provider is being unmounted for document ${documentId}. Disconnecting...`,
      );
      provider.disconnect();
    };
  }, [documentId, provider, auth.getAccessToken]);

  // A grace period is set to allow the user to
  // continue editing even while the provider is reconnecting.
  React.useEffect(() => {
    if (state.status !== 'reconnecting') {
      return;
    }

    const elapsed = Date.now() - state.disconnectedAt;
    const remaining = config.settings.reconnectGraceTimeout - elapsed;

    if (remaining <= 0) {
      provider.disconnect();
      setState({ status: 'disconnected' });
      return;
    }

    const timer = setTimeout(() => {
      provider.disconnect();
      setState({ status: 'disconnected' });
    }, remaining);
    return () => clearTimeout(timer);
  }, [state, config.settings.reconnectGraceTimeout, provider]);

  // Track unsaved changes in the document so we can show the unsaved changes indicator.
  React.useEffect(() => {
    if (state.status !== 'connected' && state.status !== 'reconnecting') {
      return;
    }

    const doc = provider.document;

    // biome-ignore lint/suspicious/noExplicitAny: Yjs update callback origin can be any type
    const trackUnsaved = (_update: Uint8Array, origin: any) => {
      if (origin !== provider) {
        setState((prev) => {
          if (prev.status === 'connected' || prev.status === 'reconnecting') {
            return { ...prev, save: { ...prev.save, saveStatus: 'unsaved' } };
          }

          return prev;
        });
      }
    };

    doc.on('update', trackUnsaved);

    return () => {
      doc.off('update', trackUnsaved);
    };
  }, [state.status, provider]);

  const userWithColor = React.useMemo(() => {
    return { ...user, color: getRandomColor() };
  }, [user]);

  return {
    state,
    provider,
    yDoc: provider.document,
    user: userWithColor,
  };
};
